import { useQuery } from "@tanstack/react-query";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { PayoutsResponse } from "@features/events/types";

export interface UsePayoutsOptions {
  enabled?: boolean;
}

type UsePayoutsResult = {
  payouts: PayoutsResponse["payouts"];
  pendingTotals: PayoutsResponse["pendingTotals"] | null;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: Error | null;
  refetchPayouts: () => void;
};

/**
 * Hook to fetch payouts data for an event organizer
 * Used in EventPayouts page and dashboard for pending payments summary
 * 
 * @param eventOrganizerId - The healthcare company ID (event organizer)
 * @param options - Query options
 * @returns Query result with payouts and pendingTotals data
 */
export const usePayouts = (
  eventOrganizerId?: string,
  options: UsePayoutsOptions = {}
): UsePayoutsResult => {
  const { GET } = useApiRequest();
  const { enabled = true } = options;

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery<PayoutsResponse>({
    queryKey: ["payouts", eventOrganizerId],
    queryFn: () =>
      GET<PayoutsResponse>(
        `/api/healthcare-companies/${eventOrganizerId}/e/finance/payouts`
      ),
    enabled: enabled && Boolean(eventOrganizerId),
    staleTime: 1000 * 60, // Cache for 1 minute
  });

  return {
    payouts: data?.payouts ?? [],
    pendingTotals: data?.pendingTotals ?? null,
    isLoading,
    isFetching,
    isError,
    error: error ?? null,
    refetchPayouts: refetch,
  } satisfies UsePayoutsResult;
};

export default usePayouts;

